import { AccessTime, Edit, Fullscreen, FullscreenExit } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useCallback, useEffect, useState, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { InputHoraHHMM } from "components/InputHoraHHMM";
import {
  IAtualizarTarefaRequest,
  ITarefaCompleta,
} from "services/modules/tarefaDetalhe";
import {

  getReferenciasEmpresaParaSelecao,
  getTodosTiposAtividade,

  IReferenciaOption,
  ITipoAtividade,
} from "services/modules/tarefas";
import {
  getResponsaveisFiltro,
  getStatusFiltro,
  IResponsavelFiltro,
  IStatusFiltro,
} from "services/modules/quadroTarefas";
import { decimalToHHMM, segundosToHHMMSS } from "shared/helpers/horasHHMM";
import { stripHtml } from "./utils/dirtyState";

// ============================================
// STYLED COMPONENTS - Conforme Mockup
// ============================================

// Componentes SectionRow, SectionLabel e ResponsavelAvatar foram removidos
// pois o campo Responsável agora usa Autocomplete múltiplo

const FieldsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const FieldContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  color: "#666",
  fontSize: "0.75rem",
  fontWeight: 500,
  textTransform: "uppercase",
}));

const EditorContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#FFF9C4", // Fundo amarelo claro conforme mockup
  borderRadius: 8,
  padding: theme.spacing(2),
  minHeight: 200,
  marginTop: theme.spacing(2),
  position: "relative",
  "& .ql-toolbar": {
    backgroundColor: "#fff",
    borderRadius: "4px 4px 0 0",
    border: "1px solid #ccc",
    borderBottom: "none",
  },
  "& .ql-container": {
    backgroundColor: "#fff",
    borderRadius: "0 0 4px 4px",
    border: "1px solid #ccc",
    minHeight: 150,
  },
  "& .ql-editor": {
    minHeight: 150,
    fontSize: "0.9rem",
  },
}));

const EditorLabel = styled(Typography)(({ theme }) => ({
  color: "#666",
  fontSize: "0.875rem",
  fontWeight: 500,
  marginBottom: theme.spacing(1),
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 1,
  backgroundColor: "#fff",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));

// ============================================
// INTERFACES
// ============================================

interface TabDetalhesProps {
  tarefa: ITarefaCompleta | null;
  formData: Partial<IAtualizarTarefaRequest>;
  editMode: boolean;
  idEmpresa: number;
  onChange: (field: string, value: any) => void;
  onRefresh: () => void;
  isCreating?: boolean;
  isSubtarefa?: boolean; // Flag para indicar se é sub-tarefa
  bloquearCampos?: boolean; // Bloqueia todos os campos exceto Status (tarefa concluída)
  // Reteste 21/08 (item 4): sem a chave "Alocar outros usuários", o campo Responsável
  // fica travado no usuário logado (criação) / nos responsáveis atuais (edição).
  podeAlocarOutros?: boolean;
}

// ============================================
// COMPONENTE
// ============================================

export const TabDetalhes = ({
  tarefa,
  formData,
  editMode,
  idEmpresa,
  onChange,
  isCreating = false,
  isSubtarefa = false,
  bloquearCampos = false,
  podeAlocarOutros = true,
}: TabDetalhesProps) => {
  // Estados para opções de seleção
  const [referencias, setReferencias] = useState<IReferenciaOption[]>([]);
  const [tiposAtividade, setTiposAtividade] = useState<ITipoAtividade[]>([]);
  const [statusList, setStatusList] = useState<IStatusFiltro[]>([]);
  const [responsaveis, setResponsaveis] = useState<IResponsavelFiltro[]>([]);


  // Estado para responsáveis selecionados (múltiplos)
  const [responsaveisSelecionados, setResponsaveisSelecionados] = useState<
    IResponsavelFiltro[]
  >([]);

  // Estados para autocomplete (removido showAddResponsavel - não mais necessário)

  // Estado para o campo Estimativa (hora) no formato HH:MM
  const [estimativaHHMM, setEstimativaHHMM] = useState("");

  // Estado para editor expandido
  const [editorExpandido, setEditorExpandido] = useState(false);

  // Handler para o campo Estimativa HH:MM (componente InputHoraHHMM)
  const handleEstimativaChange = useCallback(
    (decimal: number | undefined, hhmm: string) => {
      setEstimativaHHMM(hhmm);
      onChange("estimativaHoras", decimal);
    },
    [onChange]
  );

  // Handler para o editor Quill (normal e expandido) com guard contra a
  // normalização inicial do editor: ao montar, o Quill converte descrição
  // vazia em "<p><br></p>" e disparava onChange sem interação do usuário
  const handleDescricaoChange = useCallback(
    (value: string) => {
      const descricaoAtual = formData.descricao || "";
      if (value === descricaoAtual) return;
      // Guard robusto (item 5.3): ignora a reserialização do Quill comparando
      // apenas o TEXTO (sem tags/espaços). Cobre "<p><br></p>" vs "" e a
      // renormalização de uma descrição já preenchida ao montar o editor.
      if (stripHtml(value) === stripHtml(descricaoAtual)) return;
      onChange("descricao", value);
    },
    [formData.descricao, onChange]
  );

  // Configuração do módulo Quill
  const quillModules = useMemo(
    () => ({
      toolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  );

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
  ];

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================

  // N13.3: referência da tarefa/contexto. Na criação usa a selecionada no
  // formulário em tempo real; na edição usa a referência persistida da tarefa.
  const idReferenciaContexto = formData.idReferencia ?? tarefa?.idReferencia;

  const carregarDados = useCallback(async () => {
    const [refs, tipos, status, resp] = await Promise.all([
      getReferenciasEmpresaParaSelecao(idEmpresa),
      // Item 5.2: listar TODOS os Tipos de Atividade (Gerência > Tipos de
      // Atividades), não só os que têm gatilho na empresa. O gatilho continua
      // sendo considerado no salvamento (handleSave/executarSalvamentoComGatilho).
      getTodosTiposAtividade(),
      getStatusFiltro(),
      getResponsaveisFiltro(idEmpresa, idReferenciaContexto),
    ]);
    setReferencias(refs);
    setTiposAtividade(tipos);
    setStatusList(status);
    setResponsaveis(resp);

  }, [idEmpresa, idReferenciaContexto]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Fallback: se a tarefa em edição tiver referência fora da lista liberada
  // para a empresa, incluir a opção atual para não exibir valor órfão
  const referenciasOptions = useMemo(() => {
    if (
      tarefa?.idReferencia &&
      !referencias.some((r) => r.value === tarefa.idReferencia)
    ) {
      return [
        ...referencias,
        {
          value: tarefa.idReferencia,
          label: tarefa.nomeReferencia || `Referência ${tarefa.idReferencia}`,
        },
      ];
    }
    return referencias;
  }, [referencias, tarefa]);

  // Fallback: se a tarefa em edição tiver tipo de atividade fora da lista
  // configurada para a empresa, incluir o tipo atual
  const tiposAtividadeOptions = useMemo(() => {
    if (
      tarefa?.idTipoAtividade &&
      !tiposAtividade.some((t) => t.idTipoAtividade === tarefa.idTipoAtividade)
    ) {
      return [
        ...tiposAtividade,
        {
          idTipoAtividade: tarefa.idTipoAtividade,
          nome: tarefa.nomeTipoAtividade || `Tipo ${tarefa.idTipoAtividade}`,
          descricao: "",
          ativo: true,
        } as ITipoAtividade,
      ];
    }
    return tiposAtividade;
  }, [tiposAtividade, tarefa]);

  // Sincronizar responsáveis selecionados (todos da lista) — SEM formData nas deps:
  // este efeito só inicializa a partir do que veio do servidor; incluir o formulário
  // aqui reverteria a edição do usuário a cada mudança de chip.
  useEffect(() => {
    if (tarefa && tarefa.responsaveis.length > 0) {
      const respsSelecionados = responsaveis.filter((r) =>
        tarefa.responsaveis.some((tr) => tr.idUsuario === r.idUsuario)
      );
      setResponsaveisSelecionados(respsSelecionados);
    }
  }, [tarefa, responsaveis]);

  // Reteste 21/08 (item 4): na CRIAÇÃO o formulário pode nascer com o usuário
  // logado pré-alocado (sem a chave "Alocar outros"); o formulário é a fonte.
  useEffect(() => {
    if (!isCreating) return;
    const idsForm = formData.idsResponsaveis || [];
    setResponsaveisSelecionados(
      responsaveis.filter((r) => idsForm.includes(r.idUsuario))
    );
  }, [isCreating, responsaveis, formData.idsResponsaveis]);

  // Sincronizar campo Estimativa (hora) no formato HH:MM
  // IMPORTANTE: Só sincroniza quando a tarefa muda (carregamento), NÃO durante digitação
  useEffect(() => {
    if (tarefa && tarefa.estimativaHoras !== undefined && tarefa.estimativaHoras !== null) {
      setEstimativaHHMM(decimalToHHMM(tarefa.estimativaHoras));
    } else if (isCreating) {
      setEstimativaHHMM("");
    }
  }, [tarefa, isCreating, decimalToHHMM]);

  // ============================================
  // HANDLERS
  // ============================================
  // Funções handleAddResponsavel, handleRemoveResponsavel e getIniciais
  // foram removidas pois o campo Responsável agora usa Autocomplete único

  // ============================================
  // CÁLCULO: Campos herdados da tarefa-pai (item 1 do relatório FASE 04)
  // ============================================
  // Em subtarefas de tipologia, Solicitante/Referência/Tipo/Detalhes são herdados
  // da tarefa-pai e devem ser somente-leitura. Preferir a flag ehTipologia do VM;
  // fallback para detecção por substring no nome do tipo de atividade.
  const camposHerdadosBloqueados = useMemo(() => {
    if (!isSubtarefa) return false;
    const ehTipologiaTarefa =
      tarefa?.ehTipologia ??
      (tarefa?.nomeTipoAtividade
        ? tarefa.nomeTipoAtividade.toLowerCase().includes("tipologia")
        : false);
    return ehTipologiaTarefa;
  }, [isSubtarefa, tarefa?.ehTipologia, tarefa?.nomeTipoAtividade]);

  // Subtarefa é editável (modo edição/criação, não concluída). Reaproveitado para
  // habilitar o Select de Etapa (item 2). O gating do item 3 chega via editMode=false.
  const subtarefaEditavel = (editMode || isCreating) && !bloquearCampos;

  // Item 5.A: nas tarefas de TIPOLOGIA, as subtarefas das etapas seguintes (que NÃO
  // são a Planificação) espelham a descrição da Planificação e não têm edição própria
  // de descrição. O editor de "Detalhes da tarefa" fica somente leitura nesse caso.
  const descricaoSomenteLeitura =
    isSubtarefa &&
    (tarefa?.ehTipologia ?? false) &&
    !(tarefa?.ehPlanificacao ?? false);

  // Editor de descrição editável somente quando não estiver bloqueado por conclusão
  // nem pela regra de somente-leitura da subtarefa de tipologia. A descrição NÃO segue
  // camposHerdadosBloqueados (que continua valendo para Solicitante/Referência/Tipo):
  // descricaoSomenteLeitura já libera a Planificação e trava as etapas seguintes —
  // mantê-lo aqui bloqueava a digitação na Planificação (item 3a do relatório de 10/07).
  const descricaoEditavel =
    editMode &&
    !bloquearCampos &&
    !descricaoSomenteLeitura;

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box>
      {/* ====== SEÇÃO SOLICITANTE (Autocomplete com texto livre) ====== */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {((editMode || isCreating) && !bloquearCampos && !camposHerdadosBloqueados) ? (
          <Autocomplete
            freeSolo
            options={responsaveis}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.nome
            }
            inputValue={formData.nomeSolicitante || ""}
            value={
              formData.idSolicitante
                ? responsaveis.find((r) => r.idUsuario === formData.idSolicitante) || null
                : null
            }
            onChange={(_, newValue) => {
              if (typeof newValue === "string") {
                // Texto livre digitado (ao pressionar Enter)
                onChange("idSolicitante", undefined);
                onChange("nomeSolicitante", newValue);
              } else if (newValue) {
                // Usuário selecionado da lista
                onChange("idSolicitante", newValue.idUsuario);
                onChange("nomeSolicitante", newValue.nome);
              } else {
                // Limpar
                onChange("idSolicitante", undefined);
                onChange("nomeSolicitante", undefined);
              }
            }}
            onInputChange={(_, newInputValue, reason) => {
              // Atualiza o nomeSolicitante conforme o usuário digita
              if (reason === "input") {
                // Limpa o ID quando o usuário digita (para aceitar texto livre)
                onChange("idSolicitante", undefined);
                onChange("nomeSolicitante", newInputValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Solicitante"
                size="small"
                sx={{ minWidth: 250 }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.idUsuario}>
                {option.nome}
              </li>
            )}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              return options.filter((option) =>
                option.nome.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
            sx={{ minWidth: 250, maxWidth: 300 }}
          />
        ) : (
          <Typography variant="body2">
            <strong>Solicitante:</strong> {formData.nomeSolicitante || "-"}
          </Typography>
        )}
      </Box>

      {/* ====== SEÇÃO RESPONSÁVEL ====== */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {((editMode || isCreating) && !bloquearCampos && podeAlocarOutros) ? (
          <Autocomplete
            multiple
            options={responsaveis}
            getOptionLabel={(option) => option.nome}
            value={responsaveisSelecionados}
            onChange={(_, newValue) => {
              setResponsaveisSelecionados(newValue);
              onChange(
                "idsResponsaveis",
                newValue.map((r) => r.idUsuario)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={responsaveisSelecionados.length === 0 ? "Responsável" : ""}
                size="small"
                sx={{ minWidth: 250 }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.idUsuario}>
                {option.nome}
              </li>
            )}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              return options.filter((option) =>
                option.nome.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
            isOptionEqualToValue={(option, value) => option.idUsuario === value.idUsuario}
            sx={{ minWidth: 250, maxWidth: 400 }}
          />
        ) : (
          <Typography variant="body2">
            <strong>Responsável:</strong> {responsaveisSelecionados.length > 0 
              ? responsaveisSelecionados.map((r) => r.nome).join(", ") 
              : "-"}
          </Typography>
        )}
      </Box>

      {/* ====== LINHA 1: Referência | Status | Tipo ====== */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          marginBottom: 3,
        }}
      >
        <FieldContainer>
          <FieldLabel>Referência *</FieldLabel>
          <FormControl fullWidth size="small" disabled={!editMode || bloquearCampos || camposHerdadosBloqueados}>
            <Select
              value={formData.idReferencia || ""}
              onChange={(e) => onChange("idReferencia", e.target.value)}
            >
              <MenuItem value="">
                <em>Selecione...</em>
              </MenuItem>
              {referenciasOptions.map((ref) => (
                <MenuItem key={ref.value} value={ref.value}>
                  {ref.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldContainer>

        <FieldContainer>
          <FieldLabel>Status</FieldLabel>
          <FormControl fullWidth size="small" disabled={!editMode || isCreating}>
            <Select
              value={formData.idTarefaStatus || ""}
              onChange={(e) => onChange("idTarefaStatus", e.target.value)}
            >
              {statusList.map((status) => (
                <MenuItem key={status.idTarefaStatus} value={status.idTarefaStatus}>
                  {status.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldContainer>

        {/* Campo Tipo (sempre exibido) */}
        <FieldContainer>
          <FieldLabel>Tipo</FieldLabel>
          <FormControl fullWidth size="small" disabled={!editMode || bloquearCampos || camposHerdadosBloqueados}>
            <Select
              value={formData.idTipoAtividade || ""}
              onChange={(e) => {
                const value = e.target.value;
                // Converter para número se houver valor
                onChange("idTipoAtividade", value ? Number(value) : undefined);
              }}
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {tiposAtividadeOptions.map((tipo) => (
                <MenuItem key={tipo.idTipoAtividade} value={tipo.idTipoAtividade}>
                  {tipo.nome}
                </MenuItem>
              ))}
            </Select>
            {tiposAtividadeOptions.length === 0 && (
              <FormHelperText>
                Nenhum tipo de atividade cadastrado. Cadastre em Gerência &gt; Tipos de Atividades.
              </FormHelperText>
            )}
          </FormControl>
        </FieldContainer>

      </Box>

      {/* ====== LINHA 2: Previsão Início | Previsão Término | Estimativa ====== */}
      <FieldsGrid>
        <FieldContainer>
          <FieldLabel>Previsão de Início</FieldLabel>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={formData.previsaoInicio?.split("T")[0] || ""}
            onChange={(e) =>
              onChange("previsaoInicio", e.target.value || undefined)
            }
            disabled={!editMode || bloquearCampos}
            InputLabelProps={{ shrink: true }}
          />
        </FieldContainer>

        <FieldContainer>
          <FieldLabel>Previsão de Término</FieldLabel>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={formData.previsaoTermino?.split("T")[0] || ""}
            onChange={(e) =>
              onChange("previsaoTermino", e.target.value || undefined)
            }
            disabled={!editMode || bloquearCampos}
            InputLabelProps={{ shrink: true }}
          />
        </FieldContainer>

        <FieldContainer>
          <FieldLabel>
            Estimativa (hora)
            <AccessTime
              sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle", color: "#666" }}
            />
          </FieldLabel>
          <InputHoraHHMM
            value={estimativaHHMM}
            onChangeDecimal={handleEstimativaChange}
            disabled={!editMode || bloquearCampos}
          />
        </FieldContainer>
      </FieldsGrid>

      {/* ====== LINHA 3: Início Real | Término Real | Horas Real (Só edição) ====== */}
      {!isCreating && tarefa && (
        <FieldsGrid>
          <FieldContainer>
            <FieldLabel>Início (Real)</FieldLabel>
            <TextField
              size="small"
              fullWidth
              value={
                tarefa.inicioReal
                  ? new Date(tarefa.inicioReal).toLocaleDateString("pt-BR")
                  : "-"
              }
              disabled
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>Término (Real)</FieldLabel>
            <TextField
              size="small"
              fullWidth
              value={
                tarefa.terminoReal
                  ? new Date(tarefa.terminoReal).toLocaleDateString("pt-BR")
                  : "-"
              }
              disabled
            />
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>Horas (Real)</FieldLabel>
            <TextField
              size="small"
              fullWidth
              // Item 6.5: exibir o tempo real em hh:mm:ss (fonte: totalSegundosReal).
              value={segundosToHHMMSS(tarefa.totalSegundosReal)}
              disabled
            />
          </FieldContainer>
        </FieldsGrid>
      )}

      {/* ====== EDITOR DE TEXTO (Detalhes da Tarefa) ====== */}
      <EditorContainer>
        <EditorLabel>Detalhes da tarefa</EditorLabel>
        <ExpandButton
          size="small"
          onClick={() => setEditorExpandido(true)}
          title="Expandir editor"
        >
          <Fullscreen fontSize="small" />
        </ExpandButton>
        {descricaoEditavel ? (
          <ReactQuill
            theme="snow"
            value={formData.descricao || ""}
            onChange={handleDescricaoChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Descreva os detalhes da tarefa..."
          />
        ) : (
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: 1,
              padding: 2,
              minHeight: 150,
              border: "1px solid #ccc",
            }}
            dangerouslySetInnerHTML={{
              __html: formData.descricao || "<p>Sem descrição</p>",
            }}
          />
        )}
      </EditorContainer>

      {/* ====== MODAL EDITOR EXPANDIDO ====== */}
      <Dialog
        open={editorExpandido}
        onClose={() => setEditorExpandido(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Detalhes da tarefa
          <IconButton
            onClick={() => setEditorExpandido(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <FullscreenExit />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {descricaoEditavel ? (
            <ReactQuill
              theme="snow"
              value={formData.descricao || ""}
              onChange={handleDescricaoChange}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Descreva os detalhes da tarefa..."
              style={{ height: 400 }}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 1,
                padding: 2,
                minHeight: 400,
                border: "1px solid #ccc",
              }}
              dangerouslySetInnerHTML={{
                __html: formData.descricao || "<p>Sem descrição</p>",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
